<template>
  <aside v-if="hasUser" class="sidebar" :class="{ 'sidebar-open': isSidebarOpen }">
    <!-- Main Menu -->
    <div class="sidebar-section">
      <nav class="recursive-menu">
        <RecursiveMenuItem v-for="item in menuTree" :key="item.id" :item="item" :level="0" />
      </nav>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useSidebar } from '../composables/useSidebar';
import { getRoutesForMenu, buildMenuTree } from '../router/routeMetadata';
import { log } from '../utils/DebugLogger';
import Button from 'primevue/button';
import RecursiveMenuItem from './RecursiveMenuItem.vue';

// Get i18n and router
const { t } = useI18n();
const router = useRouter();

// Get permissionsService (injected from main.js)
const permissionsService = inject<any>('permissionsService');

// Get permissions data from service
const getPermissions = (): any => permissionsService?.getPermissionsData();

// Sidebar state
const { isSidebarOpen } = useSidebar();

// Computed
const hasUser = computed(() => {
  const permissions = getPermissions();
  return permissions && permissions.user;
});

const menuTree = computed(() => {
  log('Sidebar', 'menuTree', 'Building menu from router');

  // Get all routes from router
  const allRoutes = router.getRoutes();
  log('Sidebar', 'menuTree', `Total routes: ${allRoutes.length}`);

  // Get current permissions
  const permissions = getPermissions();

  // Filter routes suitable for menu (with permission check)
  const menuRoutes = getRoutesForMenu(allRoutes, permissions);
  log('Sidebar', 'menuTree', `Menu routes after filtering: ${menuRoutes.length}`);

  // Build hierarchical tree
  const tree = buildMenuTree(menuRoutes);
  log('Sidebar', 'menuTree', `Menu tree built with ${tree.length} top-level items`);

  return tree;
});

// Lifecycle
onMounted(() => {
  log('Sidebar', 'mounted', 'Sidebar component mounted');
  if (hasUser.value) {
    log('Sidebar', 'mounted', 'User found, menu will be built from router');
  } else {
    log('Sidebar', 'mounted', 'No user found, menu will not render');
  }
});
</script>

<style scoped>
.sidebar {
  /* Use tenant secondary color for sidebar background */
  background-color: var(--app-secondary-color);
  color: white;
  width: 250px;
  min-height: 100vh;
  border-right: 1px solid var(--app-secondary-hover-color);
  padding: 12px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

/* Mobile: Absolute positioning allows full page scroll */
@media (max-width: 768px) {
  .sidebar {
    position: absolute;
    left: -100%;
    /* Hidden by default */
    top: 0;
    /* Start from top of page */
    z-index: 9999;
    height: auto;
    min-height: 100vh;
    transition: left 0.3s ease;
  }

  .sidebar.sidebar-open {
    left: 0;
    /* Visible when open */
    box-shadow: 4px 0 15px rgba(0, 0, 0, 0.3);
  }
}

/* Desktop: Static positioning within flexbox */
@media (min-width: 769px) {
  .sidebar {
    position: static;
    display: block;
  }

  .sidebar:not(.sidebar-open) {
    width: 0;
    overflow: hidden;
    padding: 0;
    border: none;
  }
}

.sidebar-section {
  padding: 12px 0;
}

.sidebar-section:not(:last-child) {
  /* Use primary color for accent border */
  border-bottom: 1px solid rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.2);
  margin-bottom: 12px;
}

.help-btn {
  width: 100%;
  justify-content: flex-start;
  color: white !important;
  padding: 12px 16px !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
}

.help-btn:hover {
  /* Use primary color for hover effect */
  background-color: rgba(var(--app-primary-color-rgb, 230, 184, 0), 0.15) !important;
}

.menu-header {
  /* Use tenant primary color for menu headers */
  color: var(--app-primary-color);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 8px 16px;
  margin: 8px 0 8px 0;
}

/* Recursive Menu Styling */
.recursive-menu {
  width: 100%;
}

/* Mobile responsive - additional adjustments */
@media (max-width: 768px) {
  .sidebar {
    width: 280px;
    max-width: 85vw;
    /* Don't take more than 85% of screen */
  }
}

/* Very small mobile */
@media (max-width: 480px) {
  .sidebar {
    width: 260px;
    max-width: 90vw;
  }
}
</style>
